import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useParams, useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { isEmpty, map, get } from "lodash";
import {
  Box,
  Container,
  Grid,
  Typography,
  Avatar,
  Chip,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Divider,
  Skeleton,
  LinearProgress,
  IconButton,
  Button,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import CommentIcon from "@mui/icons-material/Comment";
import PersonIcon from "@mui/icons-material/Person";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DownloadIcon from "@mui/icons-material/Download";

// MUI Components
import MuiPageHeader from "../../components/mui/MuiPageHeader";
import MuiCard from "../../components/mui/MuiCard";
import MuiStatusChip from "../../components/mui/MuiStatusChip";

// Import chart
import ReactApexChart from "react-apexcharts";

// Default chart options and series
const defaultChartOptions = {
  chart: {
    type: "radialBar",
    sparkline: { enabled: true },
  },
  plotOptions: {
    radialBar: {
      hollow: { size: "60%" },
      track: { background: "#f0f0f0" },
      dataLabels: {
        name: { show: false },
        value: {
          fontSize: "20px",
          fontWeight: 600,
          color: "#556ee6",
        },
      },
    },
  },
  colors: ["#556ee6"],
};

const defaultSeries = [68];

/**
 * MuiTaskOverview - Task/Project detail page
 *
 * UX Laws Applied:
 * - Law of Proximity: Related information grouped in cards
 * - Miller's Law: Information chunked into digestible sections
 * - Jakob's Law: Standard detail page layout with sidebar
 * - Von Restorff Effect: Progress chart and status badges stand out
 * - Aesthetic-Usability Effect: Clean, professional design
 */

const ProgressSection = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  padding: theme.spacing(3),
}));

const DateItem = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "flex-start",
  gap: theme.spacing(1.5),
  marginTop: theme.spacing(2),
}));

const MuiTaskOverview = () => {
  const params = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(true);

  document.title = "Task Overview | FlexFlow ERP";

  // Redux state for project detail
  const { projectDetail } = useSelector((state) => ({
    projectDetail: state.projects?.projectDetail || {},
  }));

  useEffect(() => {
    // Simulate loading delay for UX
    const timer = setTimeout(() => {
      setLoading(false);
    }, 800);

    // Note: onGetProjectDetail would need to be imported and dispatched
    // if (params && params.id) {
    //   dispatch(onGetProjectDetail(params.id));
    // }

    return () => clearTimeout(timer);
  }, [params, dispatch]);

  // Breadcrumb configuration
  const breadcrumbs = [
    { label: "Tasks", path: "/tasks-list" },
    { label: "Task Overview" },
  ];

  // Loading state
  if (loading) {
    return (
      <div className="page-content">
        <Container maxWidth={false}>
          <MuiPageHeader
            title="Task Overview"
            breadcrumbs={breadcrumbs}
            loading={true}
          />
          <Grid container spacing={3}>
            <Grid item xs={12} lg={8}>
              <MuiCard>
                <Skeleton variant="rectangular" height={200} />
              </MuiCard>
            </Grid>
            <Grid item xs={12} lg={4}>
              <MuiCard>
                <Skeleton variant="rectangular" height={200} />
              </MuiCard>
            </Grid>
          </Grid>
        </Container>
      </div>
    );
  }

  // Empty state
  if (isEmpty(projectDetail)) {
    return (
      <div className="page-content">
        <Container maxWidth={false}>
          <MuiPageHeader
            title="Task Overview"
            breadcrumbs={breadcrumbs}
            actions={
              <Button
                variant="outlined"
                startIcon={<ArrowBackIcon />}
                onClick={() => navigate(-1)}
              >
                Go Back
              </Button>
            }
          />
          <MuiCard>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                py: 8,
              }}
            >
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No task data available
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                The task you are looking for might not exist or has been removed.
              </Typography>
              <Button
                variant="contained"
                startIcon={<ArrowBackIcon />}
                onClick={() => navigate("/tasks-list")}
              >
                Back to Tasks
              </Button>
            </Box>
          </MuiCard>
        </Container>
      </div>
    );
  }

  return (
    <div className="page-content">
      <Container maxWidth={false}>
        {/* Page Header */}
        <MuiPageHeader
          title="Task Overview"
          subtitle={projectDetail.name || "Task Details"}
          breadcrumbs={breadcrumbs}
          actions={
            <Button
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate(-1)}
            >
              Go Back
            </Button>
          }
        />

        <Grid container spacing={3}>
          {/* Left Column - Main Content */}
          <Grid item xs={12} lg={8}>
            {/* Project Details Card */}
            <MuiCard title="Project Details" sx={{ mb: 3 }}>
              <Box sx={{ display: "flex", gap: 2, mb: 3 }}>
                <Avatar
                  sx={{
                    width: 56,
                    height: 56,
                    bgcolor: "primary.main",
                    fontSize: "1.5rem",
                  }}
                >
                  {projectDetail.name?.charAt(0) || "P"}
                </Avatar>
                <Box sx={{ flex: 1, overflow: "hidden" }}>
                  <Typography variant="h6" noWrap>
                    {projectDetail.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {projectDetail.description}
                  </Typography>
                </Box>
              </Box>

              <Divider sx={{ my: 2 }} />

              <Typography variant="subtitle2" gutterBottom>
                Details:
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {get(projectDetail, "projectDetails.description", "No description available.")}
              </Typography>

              {/* Detail Points */}
              {projectDetail.projectDetails?.points && (
                <Box sx={{ mt: 2 }}>
                  {map(projectDetail.projectDetails.points, (point, index) => (
                    <Box
                      key={index}
                      sx={{
                        display: "flex",
                        alignItems: "flex-start",
                        gap: 1,
                        mb: 1,
                      }}
                    >
                      <CheckCircleIcon
                        sx={{ fontSize: 18, color: "primary.main", mt: 0.25 }}
                      />
                      <Typography variant="body2" color="text.secondary">
                        {point}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              )}

              {/* Dates */}
              <Grid container spacing={3} sx={{ mt: 2 }}>
                <Grid item xs={6} sm={4}>
                  <DateItem>
                    <CalendarTodayIcon color="primary" />
                    <Box>
                      <Typography variant="subtitle2">Start Date</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {projectDetail.startDate || "Not set"}
                      </Typography>
                    </Box>
                  </DateItem>
                </Grid>
                <Grid item xs={6} sm={4}>
                  <DateItem>
                    <EventAvailableIcon color="primary" />
                    <Box>
                      <Typography variant="subtitle2">Due Date</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {projectDetail.dueDate || "Not set"}
                      </Typography>
                    </Box>
                  </DateItem>
                </Grid>
              </Grid>
            </MuiCard>

            {/* Attached Files Card */}
            <MuiCard title="Attached Files" sx={{ mb: 3 }}>
              {projectDetail.files && projectDetail.files.length > 0 ? (
                <List disablePadding>
                  {map(projectDetail.files, (file, index) => (
                    <React.Fragment key={index}>
                      <ListItem
                        secondaryAction={
                          <IconButton edge="end" aria-label="download">
                            <DownloadIcon />
                          </IconButton>
                        }
                        sx={{ px: 0 }}
                      >
                        <ListItemAvatar>
                          <Avatar sx={{ bgcolor: "grey.100" }}>
                            <AttachFileIcon color="action" />
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary={file.name || `File ${index + 1}`}
                          secondary={file.size || "Unknown size"}
                        />
                      </ListItem>
                      {index < projectDetail.files.length - 1 && (
                        <Divider variant="inset" component="li" />
                      )}
                    </React.Fragment>
                  ))}
                </List>
              ) : (
                <Box sx={{ py: 3, textAlign: "center" }}>
                  <AttachFileIcon
                    sx={{ fontSize: 40, color: "text.disabled", mb: 1 }}
                  />
                  <Typography variant="body2" color="text.secondary">
                    No files attached
                  </Typography>
                </Box>
              )}
            </MuiCard>

            {/* Comments Card */}
            <MuiCard title="Comments">
              {projectDetail.comments && projectDetail.comments.length > 0 ? (
                <List disablePadding>
                  {map(projectDetail.comments, (comment, index) => (
                    <React.Fragment key={index}>
                      <ListItem alignItems="flex-start" sx={{ px: 0 }}>
                        <ListItemAvatar>
                          <Avatar sx={{ bgcolor: "primary.light" }}>
                            {comment.user?.charAt(0) || "U"}
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary={
                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                              }}
                            >
                              <Typography variant="subtitle2">
                                {comment.user || "Anonymous"}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {comment.date || ""}
                              </Typography>
                            </Box>
                          }
                          secondary={comment.text || comment.message || ""}
                        />
                      </ListItem>
                      {index < projectDetail.comments.length - 1 && (
                        <Divider variant="inset" component="li" />
                      )}
                    </React.Fragment>
                  ))}
                </List>
              ) : (
                <Box sx={{ py: 3, textAlign: "center" }}>
                  <CommentIcon
                    sx={{ fontSize: 40, color: "text.disabled", mb: 1 }}
                  />
                  <Typography variant="body2" color="text.secondary">
                    No comments yet
                  </Typography>
                </Box>
              )}
            </MuiCard>
          </Grid>

          {/* Right Column - Sidebar */}
          <Grid item xs={12} lg={4}>
            {/* Progress Overview Card */}
            <MuiCard title="Overview" sx={{ mb: 3 }}>
              <ProgressSection>
                <Box sx={{ width: 150, height: 150 }}>
                  <ReactApexChart
                    options={defaultChartOptions}
                    series={defaultSeries}
                    type="radialBar"
                    height={150}
                  />
                </Box>
                <Typography
                  variant="subtitle1"
                  fontWeight={600}
                  sx={{ mt: 1 }}
                >
                  Project Progress
                </Typography>
              </ProgressSection>

              <Divider sx={{ my: 2 }} />

              {/* Status */}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 2,
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  Status
                </Typography>
                <MuiStatusChip
                  status={projectDetail.status || "inprogress"}
                  size="small"
                />
              </Box>

              {/* Budget */}
              {projectDetail.budget && (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    Budget
                  </Typography>
                  <Typography variant="body2" fontWeight={500}>
                    {projectDetail.budget}
                  </Typography>
                </Box>
              )}
            </MuiCard>

            {/* Team Members Card */}
            <MuiCard title="Team Members">
              {projectDetail.team && projectDetail.team.length > 0 ? (
                <List disablePadding>
                  {map(projectDetail.team, (member, index) => (
                    <React.Fragment key={index}>
                      <ListItem sx={{ px: 0 }}>
                        <ListItemAvatar>
                          {member.img && member.img !== "Null" ? (
                            <Avatar src={member.img} alt={member.fullname} />
                          ) : (
                            <Avatar sx={{ bgcolor: "primary.main" }}>
                              {member.name?.charAt(0) ||
                                member.fullname?.charAt(0) ||
                                "U"}
                            </Avatar>
                          )}
                        </ListItemAvatar>
                        <ListItemText
                          primary={member.fullname || member.name}
                          secondary={
                            member.skills && (
                              <Box
                                sx={{
                                  display: "flex",
                                  gap: 0.5,
                                  flexWrap: "wrap",
                                  mt: 0.5,
                                }}
                              >
                                {map(member.skills, (skill, skillIndex) => (
                                  <Chip
                                    key={skillIndex}
                                    label={skill.name || skill}
                                    size="small"
                                    variant="outlined"
                                    color="primary"
                                    sx={{ height: 20, fontSize: "0.7rem" }}
                                  />
                                ))}
                              </Box>
                            )
                          }
                        />
                      </ListItem>
                      {index < projectDetail.team.length - 1 && (
                        <Divider variant="inset" component="li" />
                      )}
                    </React.Fragment>
                  ))}
                </List>
              ) : (
                <Box sx={{ py: 3, textAlign: "center" }}>
                  <PersonIcon
                    sx={{ fontSize: 40, color: "text.disabled", mb: 1 }}
                  />
                  <Typography variant="body2" color="text.secondary">
                    No team members assigned
                  </Typography>
                </Box>
              )}
            </MuiCard>
          </Grid>
        </Grid>
      </Container>
    </div>
  );
};

MuiTaskOverview.propTypes = {};

export default MuiTaskOverview;
